import React, { useEffect, useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Card, Btn } from "../../components/UI";
import { MessageSquareText, Loader2, Wand2, Check, X } from "lucide-react";
import { toast } from "sonner";

export const AiTopicsCard = () => {
  const [data, setData] = useState(null);
  const [offices, setOffices] = useState([]);
  const [officeKey, setOfficeKey] = useState("default");
  const [suggesting, setSuggesting] = useState("");
  const [suggestion, setSuggestion] = useState(null); // {topic, office_key, current_tactics, suggestion}
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get("/simple/ai-topics").then((r) => setData(r.data)).catch(() => setData({ topics: [], total: 0 }));
    api.get("/agent/playbooks").then((r) => {
      const keys = (r.data.playbooks || []).map((p) => p.key);
      setOffices(keys);
      if (keys.includes("default")) setOfficeKey("default");
      else if (keys[0]) setOfficeKey(keys[0]);
    }).catch(() => {});
  }, []);

  const suggest = async (topic) => {
    setSuggesting(topic);
    setSuggestion(null);
    try {
      const { data: s } = await api.post("/simple/ai-topics/suggest", { topic, office_key: officeKey });
      setSuggestion(s);
    } catch (e) { toast.error(errMsg(e, "Could not generate a suggestion")); }
    finally { setSuggesting(""); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const newTactics = `${suggestion.current_tactics}\n\n[Tuned from "${suggestion.topic}" questions] ${suggestion.suggestion}`;
      await api.put(`/agent/playbooks/${suggestion.office_key}`, { tactics: newTactics });
      toast.success(`Applied to the "${suggestion.office_key}" playbook tactics`);
      setSuggestion(null);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(errMsg(e, "Could not apply")); setApplying(false); }
  };

  const max = Math.max(1, ...((data?.topics || []).map((t) => t.count)));

  return (
    <Card data-testid="ai-topics-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <MessageSquareText className="w-4 h-4 text-cyan-300" /> What teams ask the AI most
        </h3>
        <div className="flex items-center gap-2">
          {data && <span className="text-[10px] uppercase tracking-widest text-slate-500">{data.total} questions analyzed</span>}
          {offices.length > 0 && (
            <select value={officeKey} onChange={(e) => setOfficeKey(e.target.value)} data-testid="topics-office-select"
              className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white focus:border-cyan-400/60 focus:outline-none">
              {offices.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Across the Campaign Agent, Ask TotalReach and Copilot — click Suggest to turn a hot topic into a one-click playbook tweak for the selected office.</p>
      {data === null ? (
        <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-cyan-300" /></div>
      ) : (data.topics || []).length === 0 ? (
        <p className="text-sm text-slate-500">No AI conversations yet.</p>
      ) : (
        <div className="space-y-3">
          {data.topics.map((t) => (
            <div key={t.topic} data-testid={`topic-row-${t.topic.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center justify-between text-sm mb-1 gap-2">
                <span className="text-slate-200">{t.topic}</span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="text-slate-500 text-xs font-mono">{t.count}</span>
                  {t.topic !== "Other" && (
                    <button onClick={() => suggest(t.topic)} disabled={!!suggesting} data-testid={`topic-suggest-${t.topic.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-[10px] uppercase tracking-wider text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1 disabled:opacity-40">
                      {suggesting === t.topic ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Suggest
                    </button>
                  )}
                </span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${Math.round((t.count / max) * 100)}%` }} />
              </div>
              {t.examples?.[0] && (
                <div className="text-[10px] text-slate-600 mt-1 italic truncate">&quot;{t.examples[0]}&quot;</div>
              )}
            </div>
          ))}
        </div>
      )}

      {suggestion && (
        <div className="mt-5 rounded-xl border border-cyan-400/30 bg-cyan-400/[0.06] p-4" data-testid="playbook-suggestion-box">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">
            Suggested tactics addition — &quot;{suggestion.office_key}&quot; playbook · from {suggestion.topic} questions
          </div>
          <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{suggestion.suggestion}</p>
          <div className="flex gap-2 mt-3">
            <Btn onClick={apply} disabled={applying} data-testid="suggestion-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply to playbook
            </Btn>
            <Btn variant="secondary" onClick={() => setSuggestion(null)} data-testid="suggestion-dismiss-btn">
              <X className="w-4 h-4" /> Dismiss
            </Btn>
          </div>
        </div>
      )}
    </Card>
  );
};
